import React from 'react';
import { APP_BUILD_TIME, APP_VERSION } from '../../constants/appVersion';
import { useServiceWorkerUpdate } from '../../providers/ServiceWorkerUpdateProvider';
import { markHardReloadAfterServiceWorkerUpdate, performHardReload } from '../../utils/hardReload';

type VersionPayload = {
  version?: unknown;
  buildTime?: unknown;
};

type ParsedSemver = {
  major: number;
  minor: number;
  patch: number;
  prerelease: string[];
};

const VERSION_ENDPOINT = '/version.json';
const VERSION_CHECK_INTERVAL_MS = 60 * 60 * 1000;
const DISMISSED_VERSION_PREFIX = 'app-update-dismissed:';
const SEMVER_PATTERN = /^v?(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z.-]+))?(?:\+[0-9A-Za-z.-]+)?$/;

function parseSemver(value: string): ParsedSemver | null {
  const match = value.trim().match(SEMVER_PATTERN);

  if (!match) {
    return null;
  }

  const [, major, minor, patch, prerelease] = match;

  return {
    major: Number(major),
    minor: Number(minor),
    patch: Number(patch),
    prerelease: prerelease ? prerelease.split('.') : [],
  };
}

function compareSemverPart(a: string, b: string): number {
  const numericA = /^\d+$/.test(a);
  const numericB = /^\d+$/.test(b);

  if (numericA && numericB) {
    return Number(a) - Number(b);
  }

  if (numericA) {
    return -1;
  }

  if (numericB) {
    return 1;
  }

  return a.localeCompare(b);
}

function compareSemver(remote: ParsedSemver, local: ParsedSemver): number {
  if (remote.major !== local.major) return remote.major - local.major;
  if (remote.minor !== local.minor) return remote.minor - local.minor;
  if (remote.patch !== local.patch) return remote.patch - local.patch;

  if (!remote.prerelease.length && local.prerelease.length) return 1;
  if (remote.prerelease.length && !local.prerelease.length) return -1;

  const maxLength = Math.max(remote.prerelease.length, local.prerelease.length);

  for (let index = 0; index < maxLength; index += 1) {
    const remotePart = remote.prerelease[index];
    const localPart = local.prerelease[index];

    if (remotePart === undefined) return -1;
    if (localPart === undefined) return 1;

    const diff = compareSemverPart(remotePart, localPart);
    if (diff !== 0) return diff;
  }

  return 0;
}

function parseBuildTimestamp(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }

  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

function isRemoteVersionNewer(
  remoteVersion: string,
  localVersion: string,
  remoteBuildTime: string | null,
  localBuildTime: string
): boolean {
  const parsedRemoteVersion = parseSemver(remoteVersion);
  const parsedLocalVersion = parseSemver(localVersion);

  if (parsedRemoteVersion && parsedLocalVersion) {
    const semverDiff = compareSemver(parsedRemoteVersion, parsedLocalVersion);
    if (semverDiff !== 0) {
      return semverDiff > 0;
    }

    // Gleiche SemVer: Buildzeit als sekundäres Signal nutzen.
    const remoteTimestamp = parseBuildTimestamp(remoteBuildTime);
    const localTimestamp = parseBuildTimestamp(localBuildTime);

    if (remoteTimestamp !== null && localTimestamp !== null) {
      return remoteTimestamp > localTimestamp;
    }

    return false;
  }

  // Falls keine SemVer genutzt wird, dient die Buildzeit als sichere Fallback-Heuristik.
  const remoteTimestamp = parseBuildTimestamp(remoteBuildTime);
  const localTimestamp = parseBuildTimestamp(localBuildTime);

  if (remoteTimestamp !== null && localTimestamp !== null) {
    return remoteTimestamp > localTimestamp;
  }

  return remoteVersion !== localVersion;
}

function getDismissedVersionKey(version: string): string {
  return `${DISMISSED_VERSION_PREFIX}${version}`;
}

function isVersionDismissed(version: string): boolean {
  try {
    return localStorage.getItem(getDismissedVersionKey(version)) === 'true';
  } catch (error) {
    console.warn('Update-Dismiss konnte nicht aus localStorage gelesen werden', error);
    return false;
  }
}

function storeDismissedVersion(version: string): void {
  try {
    localStorage.setItem(getDismissedVersionKey(version), 'true');
  } catch (error) {
    console.warn('Update-Dismiss konnte nicht in localStorage gespeichert werden', error);
  }
}

function normalizeVersion(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

export const AppUpdateBanner: React.FC = () => {
  const { isUpdateAvailable: isServiceWorkerUpdateAvailable, activateUpdate, dismissUpdate } = useServiceWorkerUpdate();
  const [remoteVersion, setRemoteVersion] = React.useState<string | null>(null);
  const [remoteBuildTime, setRemoteBuildTime] = React.useState<string | null>(null);
  const [hasVersionUpdate, setHasVersionUpdate] = React.useState(false);
  const [dismissStateToken, setDismissStateToken] = React.useState(0);

  const checkForVersionUpdate = React.useCallback(async () => {
    try {
      const response = await fetch(`${VERSION_ENDPOINT}?ts=${Date.now()}`, {
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = (await response.json()) as VersionPayload;
      const nextRemoteVersion = normalizeVersion(payload.version);
      const nextRemoteBuildTime = typeof payload.buildTime === 'string' ? payload.buildTime : null;

      if (!nextRemoteVersion) {
        console.warn('Versionsprüfung übersprungen: version.json enthält keine gültige Version.');
        return;
      }

      setRemoteVersion(nextRemoteVersion);
      setRemoteBuildTime(nextRemoteBuildTime);
      setHasVersionUpdate(
        isRemoteVersionNewer(nextRemoteVersion, APP_VERSION, nextRemoteBuildTime, APP_BUILD_TIME)
      );
    } catch (error) {
      console.warn('Versionsprüfung fehlgeschlagen', error);
    }
  }, []);

  React.useEffect(() => {
    void checkForVersionUpdate();

    const triggerCheck = () => {
      void checkForVersionUpdate();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        triggerCheck();
      }
    };

    const intervalId = window.setInterval(() => {
      void checkForVersionUpdate();
    }, VERSION_CHECK_INTERVAL_MS);

    window.addEventListener('focus', triggerCheck);
    window.addEventListener('online', triggerCheck);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', triggerCheck);
      window.removeEventListener('online', triggerCheck);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForVersionUpdate]);

  const availableVersion = React.useMemo(() => {
    if (hasVersionUpdate && remoteVersion) {
      return remoteVersion;
    }

    if (isServiceWorkerUpdateAvailable) {
      return remoteVersion ?? APP_VERSION;
    }

    return null;
  }, [hasVersionUpdate, isServiceWorkerUpdateAvailable, remoteVersion]);

  const isDismissedForVersion = React.useMemo(() => {
    if (!availableVersion || typeof window === 'undefined') {
      return false;
    }

    return isVersionDismissed(availableVersion);
  }, [availableVersion, dismissStateToken]);

  // Ein echtes SW-Update soll nicht durch ein früheres versionsgebundenes Dismiss blockiert werden.
  const shouldShowBanner = Boolean(availableVersion) && (isServiceWorkerUpdateAvailable || !isDismissedForVersion);

  const handleDismiss = React.useCallback(() => {
    if (!availableVersion) {
      return;
    }

    // Dismiss ist versionsgebunden, damit ein neues Release wieder sichtbar wird.
    storeDismissedVersion(availableVersion);
    dismissUpdate();
    setDismissStateToken((current) => current + 1);
  }, [availableVersion, dismissUpdate]);

  const handleRefresh = React.useCallback(() => {
    if (isServiceWorkerUpdateAvailable) {
      // Beim SW-Update wird nach `controllerchange` neu geladen; Marker sorgt dann für den Hard-Reload.
      markHardReloadAfterServiceWorkerUpdate();
      activateUpdate();
      return;
    }

    void performHardReload();
  }, [activateUpdate, isServiceWorkerUpdateAvailable]);

  if (!shouldShowBanner || !availableVersion) {
    return null;
  }

  const showBuildTime = typeof remoteBuildTime === 'string' && remoteBuildTime.length > 0;

  return (
    <div className="fixed left-4 right-4 top-4 z-40 sm:left-auto sm:max-w-sm">
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Neue Version verfügbar</h3>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          Verfügbar: {availableVersion} - Installiert: {APP_VERSION}
        </p>
        {showBuildTime && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Build: {remoteBuildTime}</p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={handleDismiss}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Später
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            className="rounded-md bg-primary-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-primary-700"
          >
            Aktualisieren
          </button>
        </div>
      </div>
    </div>
  );
};
